import React, { memo, type ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  Box,
  Link as DSLink,
  Loader,
  StackingAlertBanner,
} from '@saviynt/design-system';

import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../../shared/hooks';
import { type RootState } from '../../../../../../shared/types';
import { downloadBlobAsFile } from '../../../../../../shared/utils/utils';
import { licenseExportActions } from '../../../../data-layer/licenseExportSlice';
import { useLazyDownloadLicenseExportQuery } from '../../../../data-layer/licenseManagementApi';

interface DownloadLinkProps {
  exportId: string;
  isDownloading: boolean;
  downloadText: string;
  onDownload: (id: string) => Promise<void>;
}

const DownloadLink = memo(function DownloadLink({
  exportId,
  isDownloading,
  downloadText,
  onDownload,
}: DownloadLinkProps): ReactElement {
  if (isDownloading) {
    return (
      <Box className='Sod-DownloadLink-loading'>
        <Loader kind='loop' size='small' format='inline' color='inverse' />
      </Box>
    );
  }

  return (
    <DSLink
      text={downloadText}
      kind='button'
      onClick={() => onDownload(exportId)}
      color='neutral'
      suffixIconKind='Download'
      dataTheme='dark'
    />
  );
});

/**
 * Renders a success banner with a download link when a license export completes.
 * Auto-dismisses after 3 seconds. Shows a spinner during file download.
 */
const DOWNLOAD_MAX_RETRIES = 3;
const DOWNLOAD_RETRY_DELAY_MS = 3000;

function LicenseExportBanner(): ReactElement | null {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();
  const [downloadExport] = useLazyDownloadLicenseExportQuery();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const { exportId, exportStatus } = useTypedSelector(
    (state: RootState) =>
      state.aag.licenseExport ?? { exportId: null, exportStatus: null }
  );

  // Reset dismissed state when exportStatus transitions to COMPLETED
  useEffect(() => {
    if (exportStatus === 'COMPLETED' && exportId) {
      setIsDismissed(false);
    }
  }, [exportStatus, exportId]);

  const isCompleted = exportStatus === 'COMPLETED' && exportId;

  // Don't render if not completed or already dismissed
  if (!isCompleted || isDismissed) {
    return null;
  }

  const handleDownload = async (id: string): Promise<void> => {
    setIsDownloading(true);
    setDownloadError(false);

    for (let attempt = 1; attempt <= DOWNLOAD_MAX_RETRIES; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const file = await downloadExport(id).unwrap();
        const suggestedFileName = (file as unknown as { fileName?: string })
          .fileName;
        const finalFileName = suggestedFileName ?? `License_Export_${id}.xlsx`;

        downloadBlobAsFile(file, finalFileName);
        setIsDownloading(false);

        return;
      } catch (e) {
        Logger.log(
          'warn',
          `License export download attempt ${attempt} failed:`,
          e
        );

        if (attempt < DOWNLOAD_MAX_RETRIES) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise<void>((resolve) => {
            setTimeout(resolve, DOWNLOAD_RETRY_DELAY_MS);
          });
        } else {
          Logger.log(
            'error',
            'License export download failed after retries:',
            e
          );
          setDownloadError(true);
          setIsDownloading(false);
        }
      }
    }
  };

  const handleDismiss = (): void => {
    setIsDismissed(true);
    // Clear completed export from Redux state
    dispatch(licenseExportActions.clearCompletedExport());
  };

  return (
    <StackingAlertBanner
      colorTheme={downloadError ? 'Critical' : 'Success'}
      title={downloadError ? l('Download Failed') : l('Export Completed')}
      description={
        downloadError
          ? l('File could not be downloaded. Please try again.')
          : l('License export has completed successfully.')
      }
      isVisible
      onCancel={handleDismiss}
      LinkComponent={
        downloadError ? undefined : (
          <DownloadLink
            exportId={exportId}
            isDownloading={isDownloading}
            downloadText={l('Download')}
            onDownload={handleDownload}
          />
        )
      }
    />
  );
}

export default memo(LicenseExportBanner);
